// The REST surface, which is three routes.

import type { FeedbackPage, FeedbackType } from "../shared/feedback";

export interface Me {
  user: { id: string; email: string } | null;
  character: { slug: string; hp: number; gold: number } | null;
}

interface JsonResponse {
  ok: boolean;
  body: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** Network failure and unreadable JSON are one typed result instead of a
 *  rejected promise that every component has to remember to catch. */
async function jsonCall(path: string, init?: RequestInit): Promise<JsonResponse | null> {
  try {
    const res = await fetch(path, { credentials: "same-origin", ...init });
    return { ok: res.ok, body: await res.json().catch(() => null) };
  } catch {
    return null;
  }
}

function readMe(value: unknown): Me | null {
  if (!isRecord(value)) return null;
  const { user, character } = value;
  if (user === null && character === null) return { user: null, character: null };
  if (!isRecord(user) || !isRecord(character)) return null;
  if (typeof user.id !== "string" || typeof user.email !== "string") return null;
  if (typeof character.slug !== "string" || !isNumber(character.hp) || !isNumber(character.gold)) return null;
  return {
    user: { id: user.id, email: user.email },
    character: { slug: character.slug, hp: character.hp, gold: character.gold },
  };
}

/** Null means the request itself failed. An unsigned browser is a successful
 *  response whose user and character are both null. */
export async function fetchMe(): Promise<Me | null> {
  const result = await jsonCall("/api/me");
  return result?.ok ? readMe(result.body) : null;
}

export type SignInResult = { ok: true; devUrl?: string } | { ok: false; error: string };

/** The token is not optional. The server refuses a request without one, so a
 *  caller that has not been through the Turnstile gate has nothing to send and
 *  the type says so. */
export async function requestSignIn(email: string, turnstileToken: string): Promise<SignInResult> {
  const result = await jsonCall("/api/signin", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, turnstileToken }),
  });
  if (!result) return { ok: false, error: "send-failed" };
  const body = isRecord(result.body) ? result.body : {};
  if (!result.ok) return { ok: false, error: typeof body.error === "string" ? body.error : "send-failed" };
  if (body.ok !== true) return { ok: false, error: "send-failed" };
  return { ok: true, ...(typeof body.devUrl === "string" ? { devUrl: body.devUrl } : {}) };
}

// --- feedback ---------------------------------------------------------------

/** Both feedback calls answer the same three ways: here is the page, you are
 *  not signed in, or the submission was refused — and a refusal still carries
 *  the page when the server knew it, so the quota line stays honest. */
export type FeedbackResult =
  | { ok: true; page: FeedbackPage }
  | { ok: false; error: string; page?: FeedbackPage };

async function feedbackCall(path: string, init?: RequestInit): Promise<FeedbackResult> {
  const result = await jsonCall(path, init);
  if (!result) return { ok: false, error: "failed" };
  const body = isRecord(result.body) ? result.body : null;
  if (!result.ok) {
    const page = body && isFeedbackPage(body.page) ? body.page : undefined;
    return {
      ok: false,
      error: body && typeof body.error === "string" ? body.error : "failed",
      ...(page ? { page } : {}),
    };
  }
  // GET answers with the page itself; POST wraps it, because it also has to say
  // whether the send landed.
  const page = body?.page ?? body;
  return isFeedbackPage(page) ? { ok: true, page } : { ok: false, error: "failed" };
}

function isFeedbackPage(value: unknown): value is FeedbackPage {
  return isRecord(value) && isRecord(value.board) && Array.isArray(value.board.items);
}

/** One page of the board. The server clamps the number, so asking for page 900
 *  of a nine-page board answers with page nine rather than an error. */
export const fetchFeedback = (page: number | string = 1): Promise<FeedbackResult> =>
  feedbackCall(`/api/feedback?page=${encodeURIComponent(String(page))}`);

export const sendFeedback = (body: string, type: FeedbackType | null): Promise<FeedbackResult> =>
  feedbackCall("/api/feedback", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ body, type }),
  });
